import { Button } from "@mui/material";
import React, { useEffect, useState } from "react";
import { ContentURL, getContentUrl } from "../../Utils/ContentURL";
import { useAppState } from "../../../Redux/ReduxHooks";
import { BookingDataOwnership, BookingStatus } from "../../../Services/BookingEntities";
import { ShareTripDialogWrapper } from "./ShareTripDialogWrapper";
import { CancelPhoneToCnpBooking } from "../CancelPhoneToCnpBooking";
import { ConfirmationDialog } from "./ConfirmationDialog";
import { GetFixedFareCancelDialogInput, GetMeteredCancelDialogInput } from "./ConfirmationDialog/ShowConfirmationDialog";
import { Dispatch } from "../../Dispatch";
import { NoticeType, ToasterType } from "./Notifications/NotificationEntities";

/**
 * Component that displays action buttons for tracking a booking, such as sharing or canceling the trip.
 */
export const TrackingActionButtonGroup: React.FC = () => {

    const booking = useAppState(state => state.PhoneToCnp.Booking);
    const bookingStatus = booking!.TrackingInfo.BookingStatusID;
    const isPaymentConfirmed = useAppState(state => state.PhoneToCnp.IsPaymentConfirmed);

    const [shareDialogOpen, setShareDialogOpen] = useState(false);
    const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
    const [isCancelling, setIsCancelling] = useState(false);

    useEffect(() => { }, [bookingStatus]);

    const handleShareClick = () => {
        setShareDialogOpen(true);
    };

    const handleCloseShareDialog = () => {
        setShareDialogOpen(false);
    };

    const handleCancelClick = () => {
        if (isCancelling) return;
        setCancelDialogOpen(true);
    };

    const handleConfirmCancel = async () => {
        setIsCancelling(true);

        let success = false;
        try {
            success = await CancelPhoneToCnpBooking(booking!.BookingId, booking!.AccessCode, BookingDataOwnership.Guest);
        } finally {
            setIsCancelling(false);
            setCancelDialogOpen(false);
        }

        if (success) {
            // Show the success toaster
            Dispatch.PhoneToCnp.ShowToaster(ToasterType.TripCancelled);

            // update Redux state to reflect that the booking is cancelled
            const updatedBookingtracking = { ...booking!.TrackingInfo, BookingStatusID: BookingStatus.Cancelled };
            Dispatch.PhoneToCnp.RefreshTrackingInfo(updatedBookingtracking);

            // Determine if this is a fixed fare booking to show the appropriate notice
            const isFixedFare = booking!.Payment !== null || isPaymentConfirmed;

            // Show the appropriate success notice
            if (isFixedFare) {
                Dispatch.PhoneToCnp.ShowNotice(NoticeType.FixedFareTripCancelledByUser);
            } else {
                Dispatch.PhoneToCnp.ShowNotice(NoticeType.MeterFareTripCancelledByUser);
            }
        }
    };

    const handleCloseCancelDialog = () => {
        setCancelDialogOpen(false);
    };

    // Determine if this is a fixed fare booking (has payment) or metered (no payment)
    const isFixedFare = booking!.Payment !== null || isPaymentConfirmed;
    const dialogInput = isFixedFare ? GetFixedFareCancelDialogInput() : GetMeteredCancelDialogInput();

    // Don't allow sharing or cancel if the booking is opened via a read-only link
    if (booking!.DataOwnership === BookingDataOwnership.ReadOnlyLink) return null;

    // No buttons should be displayed if the booking is completed or cancelled/no job. This will be updated later to display Receipt button on Completed bookings.
    if (bookingStatus >= BookingStatus.Completed) return null;

    return (
        <div className="tracking-action-buttons">
            {bookingStatus < BookingStatus.Completed && (
                <Button
                    variant="outlined"
                    className="action-btn share-button"
                    onClick={handleShareClick}
                >
                    <img src={getContentUrl(ContentURL.images.PhoneToCnp.ShareTrip)} alt="Share" />
                    Share trip
                </Button>)}
            {bookingStatus < BookingStatus.PickedUp && (
                <Button 
                    variant="outlined" 
                    className="action-btn cancel-button"
                    onClick={handleCancelClick}
                    disabled={isCancelling}
                >
                    <img src={getContentUrl(ContentURL.images.PhoneToCnp.CloseIcon)} alt="Cancel" />
                    {isCancelling ? 'Cancelling...' : 'Cancel trip'}
                </Button>)}
            {/*<Button variant="outlined" className="action-btn receipt-button"><img src={getContentUrl(ContentURL.images.PhoneToCnp.Receipt)} />View trip receipt</Button> // This may be only enabled in Phase 2. I will confirm, hidden for now. */}

            <ShareTripDialogWrapper
                open={shareDialogOpen}
                onClose={handleCloseShareDialog}
                booking={booking!}
            />
            
            {/* Cancel trip confirmation dialog - shows different message for fixed fare vs metered */}
            <ConfirmationDialog
                open={cancelDialogOpen}
                onClose={handleCloseCancelDialog}
                title={dialogInput.title}
                message={dialogInput.message}
                additionalMessage={dialogInput.additionalMessage}
                cancelButtonText={dialogInput.cancelButtonText}
                confirmButtonText={dialogInput.confirmButtonText}
                onConfirm={handleConfirmCancel}
                confirmButtonColor={dialogInput.confirmButtonColor}
            />
        </div>
    )
}
